import { ExternalLink, HandHeart, HeartHandshake, LifeBuoy, BookOpen } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CrisisActions } from "../components/CrisisActions";
import { PlaceholderNote } from "../components/ComingSoon";
import { CRISIS, IMAGES, RESOURCES } from "../content/site";

const Section = ({ children, className = "", id, labelledBy }) => (
  <section id={id} aria-labelledby={labelledBy} className={`px-4 py-14 sm:px-8 sm:py-20 lg:px-16 ${className}`}>
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark sm:text-sm">{children}</p>
);

const H2 = ({ id, children }) => (
  <h2 id={id} className="font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
    {children}
  </h2>
);

export default function Support() {
  return (
    <>
      {/* Immediate support — first, fast, calm */}
      <section aria-labelledby="immediate-heading" className="bg-sage px-4 py-16 text-cream sm:px-8 sm:py-24 lg:px-16">
        <div className="mx-auto max-w-4xl">
          <h1 id="immediate-heading" className="font-serif text-3xl font-light leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            You deserve support <em className="text-gold-soft">right now.</em>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-cream/85">
            If you’re in the United States and need someone to talk to, call or text 988,
            or use the 988 Lifeline chat. If you or someone else is in immediate danger,
            call 911.
          </p>
          <CrisisActions variant="dark" className="mt-8" />
        </div>
      </section>

      <Section labelledBy="pathways-heading">
        <Reveal>
          <Eyebrow>Where to begin</Eyebrow>
          <H2 id="pathways-heading">Choose what’s happening</H2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {[
            { icon: LifeBuoy, title: "I’m struggling", text: "Start with the 988 options above — you can reach a real person right now, free and confidential. The directory below also lists ongoing-care resources.", anchor: null },
            { icon: HandHeart, title: "I’m worried about someone", text: "You don’t need the perfect words. See the guidance below, then explore the resource directory for what to do next.", anchor: "#supporting-someone" },
            { icon: HeartHandshake, title: "I’ve lost someone", text: "Grief after a suicide loss is its own kind of heavy. The grief and family support section below is for you.", anchor: "#grief" },
            { icon: BookOpen, title: "I want to learn how to support a person", text: "Small, caring actions matter. Start with the reminders below and the reputable guides in the directory.", anchor: "#supporting-someone" },
          ].map(({ icon: Icon, title, text, anchor }, i) => {
            const inner = (
              <>
                <Icon className="h-7 w-7 text-gold-dark" aria-hidden="true" />
                <h3 className="mt-4 font-serif text-xl font-medium text-ink sm:text-2xl">{title}</h3>
                <p className="mt-2 text-base leading-relaxed text-body">{text}</p>
              </>
            );
            return (
              <Reveal key={title} delay={i * 0.06}>
                {anchor ? (
                  <a href={anchor} className="block h-full rounded-2xl border border-sage/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-md sm:p-8" data-testid={`pathway-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {inner}
                  </a>
                ) : (
                  <div className="h-full rounded-2xl border border-sage/10 bg-white p-6 sm:p-8" data-testid={`pathway-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {inner}
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>
      </Section>

      <Section labelledBy="supporting-someone-heading" id="supporting-someone" className="bg-paper/70">
        <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12 lg:gap-16">
          <div className="md:col-span-7">
            <Reveal>
              <Eyebrow>Supporting someone</Eyebrow>
              <H2 id="supporting-someone-heading">Ask again. Listen longer.</H2>
              <div className="mt-6 space-y-4 text-lg leading-relaxed text-body">
                <p>You don’t need special training to show someone they matter.</p>
                <ul className="list-disc space-y-3 pl-6">
                  <li>Reach out — a call, a text, a quiet “I’ve been thinking about you.”</li>
                  <li>Listen without judgment, and let silence be okay.</li>
                  <li>Let them know you care, and that you’re not going anywhere.</li>
                  <li>Help them connect with qualified support — and stay with them while they do.</li>
                </ul>
                <p className="text-base">
                  For detailed guidance, the{" "}
                  <a href={CRISIS.helpSomeoneHref} target="_blank" rel="noopener noreferrer" className="font-semibold text-sage underline underline-offset-4" data-testid="help-someone-link">
                    988 Lifeline’s guide to helping someone <ExternalLink className="inline h-3.5 w-3.5" aria-hidden="true" />
                  </a>{" "}
                  is a caring, practical place to start.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal className="md:col-span-5" delay={0.1}>
            <img
              src={IMAGES.holdingHands}
              alt="Two people holding hands in a gesture of comfort and support"
              className="w-full rounded-2xl border border-sage/10 object-cover shadow-md aspect-[4/3]"
              loading="lazy"
            />
          </Reveal>
        </div>
      </Section>

      <Section labelledBy="grief-heading" id="grief">
        <Reveal>
          <Eyebrow>Grief and family support</Eyebrow>
          <H2 id="grief-heading">For those who have lost someone</H2>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-body">
            Losing someone to suicide can bring questions no one should have to carry
            alone. Support exists specifically for parents, families, and friends
            grieving this kind of loss.
          </p>
        </Reveal>
        <div className="mt-8 max-w-3xl">
          <PlaceholderNote testId="grief-resources-placeholder">
            <strong className="font-semibold text-ink">Being carefully curated.</strong>{" "}
            The family is personally reviewing grief and suicide-loss resources —
            especially for parents — before listing them here. In the meantime, the two
            grief organizations in the directory below are well established, and 988 can
            also help you find local loss-survivor support.
          </PlaceholderNote>
        </div>
      </Section>

      <Section labelledBy="directory-heading" className="bg-paper/70">
        <Reveal>
          <Eyebrow>Resource directory</Eyebrow>
          <H2 id="directory-heading">Trusted places to turn</H2>
          <p className="mt-4 max-w-3xl text-base text-body">
            Each entry shows when it was last reviewed. TJ’s Light is not affiliated with
            these organizations and does not provide clinical services.
          </p>
        </Reveal>
        <div className="mt-10 space-y-12">
          {(() => {
            const groups = [];
            RESOURCES.forEach((r) => {
              let g = groups.find((x) => x.topic === r.topic);
              if (!g) {
                g = { topic: r.topic, items: [] };
                groups.push(g);
              }
              g.items.push(r);
            });
            return groups;
          })().map((group) => (
            <div key={group.topic}>
              <h3 className="font-serif text-xl font-medium text-sage sm:text-2xl">{group.topic}</h3>
              <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-2">
                {group.items.map((r) => (
                  <Reveal key={r.name}>
                    <article className="flex h-full flex-col rounded-2xl border border-sage/10 bg-white p-6 sm:p-8" data-testid={`resource-${r.name.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}>
                      <h4 className="font-serif text-lg font-semibold text-ink sm:text-xl">{r.name}</h4>
                      <p className="mt-2 flex-1 text-base leading-relaxed text-body">{r.description}</p>
                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                        <a
                          href={r.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-sage/30 px-5 py-2 text-sm font-semibold text-sage transition-colors hover:bg-sage-soft"
                          data-testid={`resource-link-${r.name.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}
                        >
                          {r.action} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                        <p className="text-xs text-muted-foreground">Last reviewed: {r.reviewed}</p>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section labelledBy="important-note-heading">
        <Reveal className="mx-auto max-w-3xl">
          <div className="rounded-2xl border-l-4 border-gold bg-gold-soft/40 p-6 sm:p-8" role="note" data-testid="support-important-note">
            <h2 id="important-note-heading" className="font-serif text-xl font-medium text-ink">Important note</h2>
            <p className="mt-3 text-base leading-relaxed text-body">
              This website is not a crisis service. The contact form is for general
              questions and may not be monitored immediately. For immediate support, use
              the crisis options above.
            </p>
            <CrisisActions className="mt-6" />
          </div>
        </Reveal>
      </Section>
    </>
  );
}
